import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Spin, Select } from 'antd';
import { ExportOutlined, LoadingOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { updateGHGMethodology, getGHGMethodologyById, getGHGMethodologies } from '../../../api/ghg';
import RegistrySelect from './RegistrySelect';
import './edit.css';

const EditGHGMethodologyComponent: React.FC = () => {
  const { id } = useParams<{ id }>();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchGHGMethodology() {
      setLoading(true);
      try {
        const [methodologyData, methodologiesData] = await Promise.all([
          getGHGMethodologyById(id),
          getGHGMethodologies(),
        ]);
        // GET /ghg_methodologies/:id doesn't return registries, so read them off the list.
        const listed = (methodologiesData.response as any[]).find((m) => String(m.id) === String(id));
        setLoading(false);
        form.setFieldsValue({
          ...methodologyData.response,
          ...(listed ? { registry_ids: (listed.registries ?? []).map((r: { id: string }) => r.id) } : {}),
        });
      } catch (error) {
        setLoading(false);
        console.error('Error fetching GHG methodology:', error);
      }
    }

    fetchGHGMethodology();
  }, [id, form]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      await updateGHGMethodology(id, {
        name: values.display_name,
        ...(values.code !== undefined ? { code: values.code } : {}),
        include_in_project_screener: values.include_in_project_screener,
        market_label_conditions: values.market_label_conditions,
        // Left undefined (and so omitted) if the registries never loaded: omitting keeps the
        // existing ones, whereas [] would clear them.
        registry_ids: values.registry_ids,
      });
      message.success({
        content: 'GHG methodology updated successfully',
        duration: 4,
        style: { textAlign: 'right' },
      });
      navigate('/ghg_methodologies');
    } catch (error: any) {
      console.error('Error updating GHG methodology:', error);
      message.error({
        content: error.message,
        duration: 4,
        style: { textAlign: 'right' },
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Failed:', errorInfo);
  };

  return (
    <div className="update-ghg-methodology-container">
      <Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 24, color: 'white' }} spin />} fullscreen />
      <div className="form-container">
        <h1>Update GHG Methodology</h1>
        <Form
          form={form}
          name="updateGHGMethodologyForm"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
        >
          <Form.Item
            label="Name"
            name="display_name"
            rules={[{ required: true, message: 'Please input the name of the GHG methodology!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Short Display Name"
            name="code"
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Include in Project Screener"
            name="include_in_project_screener"
          >
            <Select options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }, { value: null, label: '-' }]} />
          </Form.Item>
          <Form.Item
            label="Registries"
            name="registry_ids"
          >
            <RegistrySelect />
          </Form.Item>
          <Form.Item
            label="Market Label Conditions"
            name="market_label_conditions"
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item>
            <Link to="/generic-project-type-mapper" target="_blank" rel="noopener noreferrer">
              Add project type mapping <ExportOutlined />
            </Link>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default EditGHGMethodologyComponent;
